/**
 * A path that audio takes, drawn as nodes and the hops between them.
 *
 * One visual grammar, used by the home page and by /scenarios:
 *
 *   - a hop is NEUTRAL before Sokuji and ACCENT after it, so the colour says
 *     where the translation happens without a caption;
 *   - a node is accent-outlined when it is Sokuji's own part (the app, the
 *     virtual microphone) and neutral when it is yours or theirs.
 *
 * The caller supplies nodes and the hops between them, so the invariant
 * `hops === nodes - 1` is checked here rather than trusted at each call site.
 */

import { Logo } from '@/components/ui/Logo';
import { Arrow } from './Arrow';
import './FlowChain.scss';

export type NodeKind = 'person' | 'sokuji' | 'mic' | 'app' | 'screen' | 'headphones';

export interface FlowNode {
  kind: NodeKind;
  /** Short caption under the node. */
  label: string;
}

export interface FlowHop {
  /** Language or nothing. A hop with no label is a plain step. */
  label?: string;
  /** After Sokuji. Drives the accent colour. */
  translated: boolean;
}

interface FlowChainProps {
  nodes: FlowNode[];
  hops: FlowHop[];
  /** Marks the whole chain as decorative when the surrounding copy says it. */
  label?: string;
}

const GLYPH: Record<NodeKind, JSX.Element> = {
  person: (
    <>
      <circle cx="12" cy="8.4" r="3.4" />
      <path d="M5.6 19.6c0-3.5 2.9-6.4 6.4-6.4s6.4 2.9 6.4 6.4" />
    </>
  ),
  sokuji: (
    <>
      <rect x="4" y="4" width="16" height="16" rx="4" />
      <path d="M9 15V9l3 3 3-3v6" />
    </>
  ),
  mic: (
    <>
      <rect x="9.4" y="2.8" width="5.2" height="10" rx="2.6" />
      <path d="M6.4 11.4a5.6 5.6 0 0 0 11.2 0M12 17v3.4" />
    </>
  ),
  app: (
    <>
      <rect x="2.8" y="4.6" width="18.4" height="14.8" rx="2.6" />
      <path d="M2.8 9h18.4" />
    </>
  ),
  screen: (
    <>
      <rect x="2.8" y="4.2" width="18.4" height="15.6" rx="2.6" />
      <path d="M6.6 9.4h10.8M6.6 12.8h10.8M6.6 16.2h6.6" />
    </>
  ),
  // Where a translation read aloud to a person ends: their headphones, or the
  // one earbud each person wears face-to-face.
  headphones: (
    <>
      <path d="M4.2 15.4V12a7.8 7.8 0 0 1 15.6 0v3.4" />
      <rect x="3" y="14" width="4.4" height="6.4" rx="1.8" />
      <rect x="16.6" y="14" width="4.4" height="6.4" rx="1.8" />
    </>
  ),
};

/** Sokuji's own parts carry the accent outline; yours and theirs do not. */
const OURS: ReadonlySet<NodeKind> = new Set<NodeKind>(['sokuji', 'mic']);

/**
 * CJK and Hangul, which need more than 9px to be legible.
 *
 * Sized off the label's own script rather than the page's language: the two
 * hops on a chain are two different languages by definition, so a rule keyed on
 * `[lang]` gets one of them wrong on every page. The design makes the same
 * allowance — its Japanese hop label is set at 11px beside a 9px English one.
 */
const IDEOGRAPHIC = /[\u3040-\u30ff\u3400-\u9fff\uac00-\ud7af]/;

export function FlowChain({ nodes, hops, label }: FlowChainProps) {
  if (hops.length !== nodes.length - 1) {
    throw new Error(
      `FlowChain: ${nodes.length} nodes need ${nodes.length - 1} hops, got ${hops.length}`,
    );
  }

  return (
    <div className="flow-chain" role="img" aria-label={label ?? nodes.map((n) => n.label).join(' → ')}>
      {nodes.map((node, i) => (
        <div className="flow-chain__step" key={`${node.kind}-${i}`}>
          <div className="flow-chain__node-wrap">
            <span
              className={`flow-chain__node ${OURS.has(node.kind) ? 'is-ours' : ''}`}
            >
              {/* Sokuji's own box carries the mark, not a drawn glyph: the
                  design puts the product's face on the one node that is the
                  product. */}
              {node.kind === 'sokuji' ? (
                <Logo size={19} />
              ) : (
              <svg
                width="19"
                height="19"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                {GLYPH[node.kind]}
              </svg>
              )}
            </span>
            <span className="flow-chain__label site-label">{node.label}</span>
          </div>

          {i < hops.length && (
            <div
              className={`flow-chain__hop ${hops[i].translated ? 'is-translated' : ''}`}
            >
              <span
                className={
                  'flow-chain__hop-label site-label' +
                  (IDEOGRAPHIC.test(hops[i].label ?? '') ? ' is-ideographic' : '')
                }
              >
                {hops[i].label ?? ''}
              </span>
              <Arrow className="flow-chain__arrow" />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
